import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('pointerdown', () => this.#hydrateDeferredImages(2), { once: true, signal });
    this.addEventListener('touchstart', () => this.#hydrateDeferredImages(2), { once: true, signal });
    this.addEventListener('slideshow:select', (event) => this.#handleSlideshowSelect(event), { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Loads deferred gallery images near the selected slide.
   * @param {Event} event
   */
  #handleSlideshowSelect = (event) => {
    const { detail } = /** @type {CustomEvent} */ (event);
    this.#hydrateDeferredImages(2, detail?.index);
  };

  /**
   * Hydrates deferred product gallery images without loading every Merkmal image on initial page render.
   * @param {number} count
   * @param {number} [fromIndex]
   */
  #hydrateDeferredImages(count = Infinity, fromIndex) {
    const slides = this.slideshow?.slides || [];
    const deferredImages = [];

    if (typeof fromIndex === 'number' && slides.length) {
      for (let index = fromIndex; index < Math.min(slides.length, fromIndex + count + 1); index++) {
        deferredImages.push(...slides[index].querySelectorAll('img[data-deferred-gallery-image]'));
      }
    }

    if (!deferredImages.length) {
      deferredImages.push(...this.querySelectorAll('slideshow-slide img[data-deferred-gallery-image]'));
    }

    for (const image of deferredImages.slice(0, count)) {
      const src = image.getAttribute('data-src');
      const srcset = image.getAttribute('data-srcset');
      if (srcset) image.setAttribute('srcset', srcset);
      if (src) image.setAttribute('src', src);
      image.removeAttribute('data-src');
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-deferred-gallery-image');
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
